import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import createAxiosInstance from "../../interceptors/useAxiosInterceptors";
import { react_api } from "../../config";
import { HTTP_STATUS_CODES } from "../../GrinsCostant";
import logout from "../../services/authService";
import { useSession } from '../../SessionContext';
import {
  Box,
  Flex,
  Text,
  Button,
  Image,
  Heading,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';


// Import default fallback image
import defaultServiceImg from '../images/ServicesImages/default_img.jpg';
// Import default fallback user guide
import defaultUserGuide from '../serviceComponent/ServicesUserGuides/guideExample.pdf';
// import masking from '../images/ServicesImages/masking.png';

const IntroServiceView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state;
  const [isIframeOpen, setIsIframeOpen] = useState(false);
  const [htmlPage, setHtmlPage] = useState(``);
  const [serviceImage, setServiceImage] = useState(defaultServiceImg);
  const toast = useToast();
  const { clearSessionData } = useSession();
  const [pdfUrl, setPdfUrl] = useState(defaultUserGuide);

  // Function to dynamically load service image
  useEffect(() => {
    if (item?.userguide_name) {
      import(`../serviceComponent/ServicesUserGuides/${item.userguide_name}`)
        .then(m => setPdfUrl(m.default))
        .catch(err => {
          console.warn(`Impossibile caricare ${item.userguide_name}:`, err);
          setPdfUrl(defaultUserGuide);
        });
    }
    if (item && item.img_name) {
      try {
        // Dynamically import the image based on the img_name from the API
        import(`../images/ServicesImages/${item.img_name}`)
          .then(imageModule => {
            setServiceImage(imageModule.default);
          })
          .catch(error => {
            console.warn(`Failed to load image ${item.img_name}:`, error);
            // Keep the default image if the specific image fails to load
          });
      } catch (error) {
        console.warn(`Error importing image ${item.img_name}:`, error);
        // Keep using the default image if there's an error
      }
    }
  }, [item]);

  const axiosInstance = createAxiosInstance(async (axiosInstance) => {
    logout(t, clearSessionData, navigate, toast, axiosInstance);
  });

  const fetchInternalService = async () => {
    try {
      // Check if item and item.service_url exist
      if (!item || !item.service_url) {
        toast({
          title: t('toastMsg.error'),
          description: t('toastMsg.missingServiceUrl'),
          position: "top",
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const url = item.service_url;
      if (!/^https?:\/\//i.test(url)) {
        navigate(url +"?hideHeader=true&hideNavbar=true", { state: { title: item.title } });
        return;
      }

      const requestData = {
        BearerToken: sessionStorage.getItem("token"),
        username: sessionStorage.getItem("username"),
        urlService: item.service_url // Use the URL from the specific service
      };
      const options = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const urlFetch = `${react_api}/services/getInternalServiceADP`;
      const response = await axiosInstance.post(urlFetch, requestData, options);

      const result = response.data;

      if (response.status.toString() === HTTP_STATUS_CODES.OK) {
        setHtmlPage(result);
        setIsIframeOpen(true);
      } else {
        toast({
          title: t('toastMsg.error'),
          description: result.message ? t(`messageBE.${result.message}`) : t('toastMsg.genericError'),
          position: "top",
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: t('toastMsg.callError'),
        description: String(error),
        position: "top",
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    // <Flex direction="column" minH="100vh" w="100%" bg="gray.50" overflow="auto" px="0" position="relative">
    <Flex direction="column" minH="100%" w="100%" bg="gray.50" overflow="auto" px="0" position="relative">
      {isIframeOpen ? (
        <Box w="100%" h="100%" dangerouslySetInnerHTML={{ __html: htmlPage }} />
      ) : (
        <>
          <Box w="100%" p={4} mx="auto" flex="0 0 auto">
            <Flex direction="row" align="flex-start" justify="flex-start" wrap="wrap" w="100%" gap={6}>
              <Box paddingLeft={0.25} paddingTop={1} w="200px" mx="0">
                <Image
                  src={serviceImage}
                  alt={`${item?.title || 'Service'} Image`}
                  borderRadius="md"
                  shadow="md"
                  maxW="100%"
                  h="auto"
                  fallback={<Box w="100%" h="120px" bg="gray.200" borderRadius="md" />}
                />
              </Box>
              <Flex direction="column" flex="1" align="flex-start" justify="flex-start" h="100%" w="100%">
                <Heading as="h1" size="md" mb={4}>{item?.title}</Heading>
                <Box w="100%" p={2} border="1px solid #ddd" borderRadius="md" bg="white" h="113px" overflowY="auto" overflowX="hidden">
                  <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.700">{item?.description}</Text>
                </Box>
                <Flex direction="row" gap={4} mt={4}>
                  <Button colorScheme="teal" size="sm" onClick={fetchInternalService}>
                    {t('services.accessService')}
                  </Button>
                  <Button colorScheme="gray" size="sm" variant="outline" onClick={() => navigate("/my-services?hideHeader=true&hideNavbar=true")}>
                    {t('services.backServicesCatalog')}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Box>
          <Box flex="1" w="100%" bg="white" p={4} minH="842px" mb="100px">
            <Box w="100%" h="100%" borderRadius="md" border="1px solid #ddd">
              <iframe
                src={pdfUrl}
                title="PDF Viewer"
                style={{ width: "100%", height: "842px", border: 'none', borderRadius: '0.5rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' }}
              />
            </Box>
          </Box>
        </>
      )}
    </Flex>
  );
};

export default IntroServiceView;